import { useCallback, useState } from 'react'
import { ToolbarActionType } from 'Core/Workbench'

export type InfoElementType = {
    id: string
    element: React.ReactElement
    alignment: 'left' | 'right' | 'center'
    placement?: 'start' | 'end'
}

export type ToolbarAccessorType = {
    /**
     * Array of toolbar actions
     */
    actions: ToolbarActionType[]
    /**
     * Adds a new action to the toolbar
     * @param {ToolbarActionType} item - The action to add
     * @param {'start' | 'end'} [placement] - Optional placement of the action
     */
    addAction: (item: ToolbarActionType, placement?: 'start' | 'end') => void
    /**
     * Removes an action from the toolbar by its unique identifier
     * @param {string} id - The unique identifier of the action to remove
     */
    removeAction: (id: string) => void
    /**
     * Updates an existing action in the toolbar
     * @param {string} id - The unique identifier of the action to update
     * @param {Partial<ToolbarActionType>} option - The properties to update on the action
     */
    updateAction: (id: string, option: { [key: string]: string | boolean }) => void
    /**
     * Adds a new info element to the toolbar
     * @param {InfoElementType} element - The info element to add
     */
    addInfoElement: (element: InfoElementType) => void
    /**
     * Removes an info element from the toolbar by its unique identifier
     * @param {string} id - The unique identifier of the info element to remove
     */
    removeInfoElement: (id: string) => void
    /**
     * Array of info elements
     */
    infoElements: InfoElementType[]
}

export const useToolbar = (): ToolbarAccessorType => {
    const [actions, setActions] = useState<ToolbarActionType[]>([])
    const [infoElements, setInfoElements] = useState<InfoElementType[]>([])

    const addAction = useCallback((item: ToolbarActionType, placement: 'start' | 'end' = 'end') => {
        setActions(curr => {
            return placement === 'end' ? [...curr, item] : [item, ...curr]
        })
    }, [])

    const removeAction = useCallback((id: string) => {
        setActions(curr => {
            return curr.filter(item => item.id !== id)
        })
    }, [])

    const updateAction = useCallback(
        (id: string, option: Partial<ToolbarActionType>) => {
            const action = actions.find(item => item.id === id)
            if (action) {
                setActions(curr => {
                    return curr.map(item => (item.id === id ? { ...item, ...option } : item))
                })
            }
        },
        [actions]
    )

    const addInfoElement = useCallback((element: InfoElementType) => {
        setInfoElements(curr => {
            return element.placement === 'end' ? [...curr, element] : [element, ...curr]
        })
    }, [])

    const removeInfoElement = useCallback((id: string) => {
        setInfoElements(curr => {
            return curr.filter(item => item.id !== id)
        })
    }, [])

    return {
        addAction,
        removeAction,
        updateAction,
        actions,
        addInfoElement,
        removeInfoElement,
        infoElements,
    }
}
